import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getInterviews } from '../../lib/interviewsClient';

const InterviewRequestAlert = ({ onOpenModal }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingCount();
  }, []);

  const fetchPendingCount = async () => {
    try {
      const interviews = await getInterviews();

      const matchRequests = (interviews || []).filter(
        (interview) => interview.fields["Stage"] === "Match requested"
      );

      setPendingCount(matchRequests.length);
    } catch (error) {
      console.error("Error fetching pending match requests count:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequests = () => {
    if (onOpenModal) {
      onOpenModal();
    } else {
      navigate('/interviews');
    }
  };

  // Don't show if no pending requests, still loading, or dismissed
  if (loading || pendingCount === 0 || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-4 left-1/2 transform -translate-x-1/2 md:left-[calc(50%-8rem)] md:transform-none md:-translate-x-0 z-50 max-w-md"
      >
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg backdrop-blur-sm">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              {/* Icon and content */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0 relative">
                  <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C13.1 2 14 2.9 14 4V5.08C16.84 5.56 19 8.03 19 11V16L21 18V19H3V18L5 16V11C5 8.03 7.16 5.56 10 5.08V4C10 2.9 10.9 2 12 2ZM12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z"/>
                  </svg>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse">
                    <div className="absolute inset-0 rounded-full bg-red-500 animate-ping"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {pendingCount} interview request{pendingCount !== 1 ? 's' : ''} waiting
                  </p>
                  <p className="text-xs text-gray-500">
                    Companies want to interview you
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleViewRequests}
                  className="px-3 py-1.5 bg-[#02B491] text-white text-xs font-medium rounded-lg hover:bg-[#029078] transition-colors"
                >
                  Review
                </button>
                <button
                  onClick={() => setDismissed(true)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                  aria-label="Dismiss notification"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Subtle accent border */}
          <div className="h-0.5 bg-gradient-to-r from-[#02B491] to-[#029078]"></div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InterviewRequestAlert; 